/**
 * Dev-tools override for the last week of complimentary Heidi 2 Max usage.
 *
 * Same shape as the billing override (`./overrides.ts`): a sessionStorage-backed
 * atom read by `useComplimentaryTrialEndState`, so the sidebar countdown, the
 * strip banner and the T-7 modal all render the forced state without a real
 * allowance, flag or opt-in cohort.
 *
 * Production safety: `applyComplimentaryTrialEndDevOverride` returns `null`
 * whenever `getEnvironment()` reports production, read per call.
 */
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import {
  COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT,
  type ComplimentaryTrialEndState,
  getComplimentaryDaysLeft,
} from '../query/complimentary-trial-end.utils';

export type ComplimentaryTrialEndDevOverride = {
  /** Fixed when the override is set, so banner dismissal keys on a stable end. */
  cycleEnd: string;
  usagePercent: number;
  /** Banner dismissals saved before this are ignored, so the banner can be shown again. */
  bannerDismissalResetAt?: string;
};

const sessionStorageAdapter = createJSONStorage<ComplimentaryTrialEndDevOverride | null>(
  () => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.sessionStorage;
  }
);

export const complimentaryTrialEndDevOverrideAtom =
  atomWithStorage<ComplimentaryTrialEndDevOverride | null>(
    'heidi.devTools.complimentaryTrialEndOverride',
    null,
    sessionStorageAdapter,
    { getOnInit: true }
  );

/** The forced last-week state, or `null` when no override applies. */
export function applyComplimentaryTrialEndDevOverride(
  override: ComplimentaryTrialEndDevOverride | null,
  now: Date
): ComplimentaryTrialEndState | null {
  if (override == null || getEnvironment() === ENV_NAME.PROD) {
    return null;
  }
  const daysLeft = getComplimentaryDaysLeft({ cycle_end: override.cycleEnd }, now);
  if (daysLeft == null) {
    return null;
  }
  return {
    daysLeft,
    cycleEnd: override.cycleEnd,
    usagePercent: override.usagePercent,
    cohort:
      override.usagePercent >= COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT
        ? 'active'
        : 'inactive',
  };
}
